import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Stack,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Collapse,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

// Estilo del chip por estado de cobro. Aprobado = verde; fallos = rojo (semánticos).
export const CHARGE_STATUS = {
  APPROVED: { label: 'Aprobado', color: '#34d399', bg: 'rgba(27,147,75,0.16)', icon: CheckIcon },
  DECLINED: { label: 'Rechazado', color: '#ff8a8a', bg: 'rgba(178,22,22,0.16)', icon: CloseIcon },
  ERROR: { label: 'Error', color: '#ff8a8a', bg: 'rgba(178,22,22,0.16)', icon: CloseIcon },
  VOIDED: { label: 'Anulado', color: '#ff8a8a', bg: 'rgba(178,22,22,0.16)', icon: CloseIcon },
  PENDING: { label: 'Pendiente', color: 'text.secondary', bg: 'rgba(128,128,128,0.16)', icon: HourglassEmptyIcon },
};

export const formatChargeDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

function DetailRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>{label}</Typography>
      <Box component="span" sx={{ fontWeight: 600, fontSize: '0.9rem', color: 'text.primary', textAlign: 'right' }}>{value}</Box>
    </Stack>
  );
}

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

/**
 * Ítem de cobro estilo app de movimientos (Nequi): círculo de estado, plan + fecha,
 * monto a la derecha coloreado. Tap expande el detalle (intento / razón). Sin scroll
 * horizontal: pensado para mobile.
 */
function ChargeListItem({ charge, t, showPlan, expanded, onToggle }) {
  const cs = CHARGE_STATUS[charge.status] || CHARGE_STATUS.PENDING;
  const StatusIcon = cs.icon;

  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'hairline.main', '&:last-of-type': { borderBottom: 0 } }}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        onClick={onToggle}
        sx={{ px: 1.5, py: 1.25, cursor: 'pointer', '&:active': { backgroundColor: 'surface.overlay' } }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '50%',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: cs.bg,
          }}>
          <StatusIcon sx={{ fontSize: 18, color: cs.color }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }} noWrap>
            {showPlan && charge.plan_name
              ? charge.plan_name
              : t(`subscription.manage.chargeStatus.${charge.status}`, { defaultValue: cs.label })}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {formatChargeDate(charge.created_at)}
            {showPlan && charge.plan_name && (
              <> · {t(`subscription.manage.chargeStatus.${charge.status}`, { defaultValue: cs.label })}</>
            )}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: cs.color, fontWeight: 800, whiteSpace: 'nowrap' }}>
          ${Number(charge.amount).toLocaleString('es-CO')}
        </Typography>
        <KeyboardArrowDownIcon
          sx={{ fontSize: 18, color: 'text.disabled', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 200ms cubic-bezier(0.32,0.72,0,1)' }}
        />
      </Stack>
      <Collapse in={expanded} unmountOnExit>
        <Stack spacing={0.75} sx={{ px: 1.5, pb: 1.5 }}>
          <DetailRow label={t('subscription.manage.chargeAmount')} value={`$${Number(charge.amount).toLocaleString('es-CO')} ${charge.currency}`} />
          <DetailRow label={t('subscription.manage.attempt')} value={String(charge.attempt_number ?? 1)} />
          {charge.error_reason && (
            <DetailRow label={t('subscription.manage.reason')} value={charge.error_reason} />
          )}
        </Stack>
      </Collapse>
    </Box>
  );
}

ChargeListItem.propTypes = {
  charge: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  showPlan: PropTypes.bool.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

/**
 * Historial de cobros de suscripción.
 * - Desktop (md+): tabla con filas expandibles y paginación.
 * - Mobile: lista estilo Nequi (sin scroll horizontal) con "Cargar más".
 * Modos de datos:
 * - cliente (default): recibe todos los `charges` y pagina localmente.
 * - servidor: `serverSide` + `total` + `page` + `onPageChange` (desktop, 0-based)
 *   y `onLoadMore`/`hasMore`/`loadingMore` (mobile, acumulativo).
 */
function ChargesHistoryTable({
  charges,
  t,
  serverSide = false,
  total = 0,
  page = 0,
  onPageChange = null,
  rowsPerPage = 25,
  showPlan = false,
  onLoadMore = null,
  hasMore = false,
  loadingMore = false
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [localPage, setLocalPage] = useState(0);
  const [localVisible, setLocalVisible] = useState(rowsPerPage);
  const [expanded, setExpanded] = useState(null);

  const shellSx = { mt: 1, borderRadius: 2, border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.overlay', overflow: 'hidden' };

  /* ------------------------------- MOBILE: lista Nequi ------------------------------- */
  if (isMobile) {
    const items = serverSide ? charges : charges.slice(0, localVisible);
    const showLoadMore = serverSide ? hasMore : charges.length > localVisible;

    return (
      <Box sx={shellSx}>
        {items.map((c) => (
          <ChargeListItem
            key={c.id}
            charge={c}
            t={t}
            showPlan={showPlan}
            expanded={expanded === c.id}
            onToggle={() => setExpanded(expanded === c.id ? null : c.id)}
          />
        ))}
        {showLoadMore && (
          <Box sx={{ p: 1.25 }}>
            <Button
              fullWidth
              disabled={Boolean(loadingMore)}
              onClick={() => (serverSide ? onLoadMore?.() : setLocalVisible((v) => v + rowsPerPage))}
              sx={{ color: 'gold.main', fontWeight: 700, textTransform: 'none', '&:hover': { backgroundColor: 'rgba(249,189,78,0.08)' } }}>
              {loadingMore
                ? <CircularProgress size={18} sx={{ color: 'gold.main' }} />
                : t('subscription.manage.loadMore', { defaultValue: 'Cargar más' })}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  /* ------------------------------- DESKTOP: tabla ------------------------------- */
  const currentPage = serverSide ? page : localPage;
  const count = serverSide ? total : charges.length;
  const paged = serverSide
    ? charges
    : charges.slice(localPage * rowsPerPage, localPage * rowsPerPage + rowsPerPage);

  const handlePageChange = (_, p) => {
    setExpanded(null);
    if (serverSide) {
      onPageChange(p);
    } else {
      setLocalPage(p);
    }
  };

  const headSx = { color: 'text.secondary', fontWeight: 700, borderColor: 'hairline.main' };

  return (
    <Box sx={shellSx}>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>{t('subscription.manage.chargeStatusCol')}</TableCell>
              <TableCell sx={headSx}>{t('subscription.manage.chargeDate')}</TableCell>
              {showPlan && <TableCell sx={headSx}>{t('subscription.manage.chargePlan', { defaultValue: 'Plan' })}</TableCell>}
              <TableCell align="right" sx={headSx}>{t('subscription.manage.chargeAmount')}</TableCell>
              <TableCell sx={{ borderColor: 'hairline.main' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.map((c) => {
              const cs = CHARGE_STATUS[c.status] || CHARGE_STATUS.PENDING;
              const isOpen = expanded === c.id;
              const cellSx = { color: 'text.primary', borderColor: 'hairline.main' };
              const colSpan = showPlan ? 5 : 4;
              return (
                <React.Fragment key={c.id}>
                  <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpanded(isOpen ? null : c.id)}>
                    <TableCell sx={{ borderColor: 'hairline.main' }}>
                      <Chip
                        size="small"
                        label={t(`subscription.manage.chargeStatus.${c.status}`, { defaultValue: cs.label })}
                        sx={{ backgroundColor: cs.bg, color: cs.color, fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell sx={cellSx}>{formatChargeDate(c.created_at)}</TableCell>
                    {showPlan && <TableCell sx={cellSx}>{c.plan_name || '—'}</TableCell>}
                    <TableCell align="right" sx={{ ...cellSx, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      ${Number(c.amount).toLocaleString('es-CO')} {c.currency}
                    </TableCell>
                    <TableCell padding="checkbox" sx={{ borderColor: 'hairline.main' }}>
                      <KeyboardArrowDownIcon
                        sx={{ color: 'text.secondary', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 200ms cubic-bezier(0.32,0.72,0,1)' }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={colSpan} sx={{ py: 0, border: 0 }}>
                      <Collapse in={isOpen} unmountOnExit>
                        <Stack spacing={0.75} sx={{ py: 1.5, px: 1 }}>
                          <DetailRow label={t('subscription.manage.attempt')} value={String(c.attempt_number ?? 1)} />
                          {c.error_reason && (
                            <DetailRow label={t('subscription.manage.reason')} value={c.error_reason} />
                          )}
                        </Stack>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      {count > rowsPerPage && (
        <TablePagination
          component="div"
          count={count}
          page={currentPage}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
        />
      )}
    </Box>
  );
}

ChargesHistoryTable.propTypes = {
  charges: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
  serverSide: PropTypes.bool,
  total: PropTypes.number,
  page: PropTypes.number,
  onPageChange: PropTypes.func,
  rowsPerPage: PropTypes.number,
  showPlan: PropTypes.bool,
  onLoadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  loadingMore: PropTypes.bool,
};

export default ChargesHistoryTable;
